/**
 * Application Constants
 */

// Auto-detect API URL based on window.location for mobile device testing
const getApiUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // If accessing from non-localhost (e.g., mobile device), use the host's IP
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}:3000`;
  }
  
  return 'http://localhost:3000';
};

const getWsUrl = () => {
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }
  
  // If accessing from non-localhost (e.g., mobile device), use the host's IP
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    const hostname = window.location.hostname;
    return `ws://${hostname}:3000`;
  }
  
  return 'ws://localhost:3000';
};

export const API_URL = getApiUrl();
export const WS_URL = getWsUrl();

// Auth
export const TOKEN_KEY = 'dp_rider_token';
export const REFRESH_TOKEN_KEY = 'dp_rider_refresh_token';
export const FINGERPRINT_KEY = 'dp_rider_fingerprint';

// Performance
export const GPS_UPDATE_INTERVAL = 5000; // 5 seconds
export const GPS_MIN_DISTANCE = 50; // 50 meters (Haversine filter)
export const GPS_HIGH_ACCURACY = true;
export const GPS_TIMEOUT = 10000; // 10 seconds
export const GPS_MAXIMUM_AGE = 5000; // 5 seconds

// Offline
export const OFFLINE_QUEUE_KEY = 'dp_rider_offline_queue';
export const MAX_OFFLINE_ACTIONS = 100;
export const RETRY_ATTEMPTS = 3;
export const RETRY_DELAY_BASE = 1000; // 1 second

// WebSocket
export const WS_RECONNECT_INTERVAL = 3000;
export const WS_MAX_RECONNECT_ATTEMPTS = 10;
export const WS_HEARTBEAT_INTERVAL = 30000; // 30 seconds

// UI
export const TOAST_DURATION = 3000;
export const DEBOUNCE_DELAY = 300;
export const THROTTLE_DELAY = 1000;

// Order statuses
export const ORDER_STATUS = {
  AVAILABLE: 'available',
  ACCEPTED: 'accepted',
  PICKED_UP: 'picked_up',
  DELIVERING: 'delivering',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
} as const;

export type OrderStatus = typeof ORDER_STATUS[keyof typeof ORDER_STATUS];

// Rider statuses
export const RIDER_STATUS = {
  OFFLINE: 'offline',
  AVAILABLE: 'available',
  BUSY: 'busy',
  SUSPENDED: 'suspended',
} as const;

export type RiderStatus = typeof RIDER_STATUS[keyof typeof RIDER_STATUS];

// Zones
export const ZONES = {
  LEGAZPI: 'legazpi',
  DARAGA: 'daraga',
  OLD_ALBAY: 'old_albay',
} as const;

export type Zone = typeof ZONES[keyof typeof ZONES];

// Sprint 4I: Rating & Review types
export interface RiderRating {
  id: string;
  order_id: string;
  buyer_user_id: string;
  rating_value: number; // 1-5
  comment?: string;
  review_image_id?: string;
  created_at: string;
}

export interface RiderRatingStats {
  avg_rating: number;
  ratings_count: number;
  reliability_score: number;
  distribution?: { [key: number]: number };
}

export type ReliabilityBadge = 'bronze' | 'silver' | 'gold' | 'platinum';










// WebSocket channels
export const WS_CHANNELS = {
  ORDERS: 'order-updates',
  CHAT: 'chat-messages',
  CHAT_NEW: 'chat:new',
  CHAT_DELIVERED: 'chat:delivered',
  CHAT_READ: 'chat:read',
  CHAT_TYPING: 'chat:typing',
  ALERTS: 'system-alerts',
  LOCATION: 'rider-location',
  GPS_TRACKING: 'gps-tracking',
} as const;
